import type {
  AiChangeGroupEntry,
  ChangeEntry,
  FieldChange,
  RerunEntry,
  ScheduleEntry,
  SchedulerNoteEntry,
  StopEntry,
  UserSummary,
} from '@/lib/api/generated/model';

import type { RunActivityEvent } from './runActivity';

export type ActivityChange = ChangeEntry;
export type ActivityGroup = AiChangeGroupEntry;
export type ActivityUser = UserSummary;
export type ActivityFieldChange = FieldChange;
export type ActivityRerun = RerunEntry;
export type ActivityStop = StopEntry;
export type ActivitySchedule = ScheduleEntry;
export type ActivityNote = SchedulerNoteEntry;
export type ActivityEntry =
  | ActivityChange
  | ActivityGroup
  | ActivityRerun
  | ActivityStop
  | ActivitySchedule
  | ActivityNote;

export const isGroup = (e: ActivityEntry): e is ActivityGroup =>
  e.kind === 'ai_group';
export const isChange = (e: ActivityEntry): e is ActivityChange =>
  e.kind === 'change';
export const isRerun = (e: ActivityEntry): e is ActivityRerun =>
  e.kind === 'rerun';
export const isStop = (e: ActivityEntry): e is ActivityStop =>
  e.kind === 'stop';
export const isSchedule = (e: ActivityEntry): e is ActivitySchedule =>
  e.kind === 'schedule';
export const isNote = (e: ActivityEntry): e is ActivityNote =>
  e.kind === 'note';

export type FeedItem =
  | { type: 'day'; id: string; label: string }
  | { type: 'progress'; id: string; at: string; event: RunActivityEvent }
  | {
      type: 'grouped-progress';
      id: string;
      at: string;
      stepName: string | null;
      count: number;
      events: RunActivityEvent[];
    }
  | { type: 'change'; id: string; at: string; entry: ActivityChange }
  | { type: 'ai-group'; id: string; at: string; group: ActivityGroup }
  | { type: 'rerun'; id: string; at: string; entry: ActivityRerun }
  | { type: 'stop'; id: string; at: string; entry: ActivityStop }
  | { type: 'schedule'; id: string; at: string; entry: ActivitySchedule }
  | { type: 'note'; id: string; at: string; entry: ActivityNote };

export interface FeedOptions {
  /** Injected so "Today" and "Yesterday" are testable. */
  now: Date;
  showProgress: boolean;
}

/** Entered-step events of one step this close together read as one line. */
const GROUP_WINDOW_MS = 60_000;

type Dated = Exclude<FeedItem, { type: 'day' }>;

function dayLabel(at: string, now: Date): string {
  const date = new Date(at);
  const startOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * The run's feed: progress events (computed in the browser by `runActivity`)
 * and subtask changes (served by the service), newest first.
 *
 * A `task-created` event is dropped when a `create` change names the same
 * subtask — the change says it and names who did it; runs that predate the
 * log have no change and keep the event. Consecutive `entered-step` events
 * of one step within a minute become one line, and a separator precedes each
 * day.
 */
export function buildActivityFeed(
  events: RunActivityEvent[],
  entries: ActivityEntry[],
  { now, showProgress }: FeedOptions,
): FeedItem[] {
  const allChanges = entries.flatMap((entry) =>
    isGroup(entry) ? entry.changes : isChange(entry) ? [entry] : [],
  );
  // A rerun entry already says the run started, and by whom.
  const rerunExecutions = new Set(
    entries.filter(isRerun).map((e) => e.execution_id),
  );
  const created = new Set(
    allChanges.filter((c) => c.op === 'create').map((c) => c.subtask_id),
  );

  const items: Dated[] = [];
  if (showProgress) {
    for (const event of events) {
      if (
        event.kind === 'fan-out-started' &&
        rerunExecutions.has(event.id.replace('fan-out:', ''))
      ) {
        continue;
      }
      if (
        event.kind === 'task-created' &&
        event.taskId &&
        created.has(event.taskId)
      ) {
        continue;
      }
      items.push({ type: 'progress', id: event.id, at: event.at, event });
    }
  }
  for (const entry of entries) {
    if (isGroup(entry)) {
      items.push({
        type: 'ai-group',
        id: `group:${entry.execution_id}`,
        at: entry.at,
        group: entry,
      });
    } else if (isChange(entry)) {
      items.push({
        type: 'change',
        id: `change:${entry.id}`,
        at: entry.at,
        entry,
      });
    } else if (isRerun(entry)) {
      items.push({
        type: 'rerun',
        id: `rerun:${entry.execution_id}`,
        at: entry.at,
        entry,
      });
    } else if (isSchedule(entry)) {
      items.push({
        type: 'schedule',
        id: `schedule:${entry.execution_id}`,
        at: entry.at,
        entry,
      });
    } else if (isNote(entry)) {
      items.push({
        type: 'note',
        id: `note:${entry.id}`,
        at: entry.at,
        entry,
      });
    } else {
      items.push({
        type: 'stop',
        id: `stop:${entry.execution_id}`,
        at: entry.at,
        entry,
      });
    }
  }
  // Newest first. Array.sort is stable, so equal times keep the order they
  // were pushed in: progress events (already ranked by `runActivity`), then
  // changes.
  items.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  const collapsed: Dated[] = [];
  for (const item of items) {
    const last = collapsed[collapsed.length - 1] as Dated | undefined;
    if (
      item.type === 'progress' &&
      item.event.kind === 'entered-step' &&
      last &&
      (last.type === 'progress' || last.type === 'grouped-progress') &&
      (last.type === 'progress' ? last.event : last.events[0]).kind ===
        'entered-step' &&
      (last.type === 'progress' ? last.event : last.events[0]).stepName ===
        item.event.stepName &&
      new Date(
        last.type === 'progress'
          ? last.at
          : last.events[last.events.length - 1].at,
      ).getTime() -
        new Date(item.at).getTime() <=
        GROUP_WINDOW_MS
    ) {
      const members = last.type === 'progress' ? [last.event] : last.events;
      collapsed[collapsed.length - 1] = {
        type: 'grouped-progress',
        id: `grouped:${members[0].id}`,
        at: last.at,
        stepName: item.event.stepName,
        count: members.length + 1,
        events: [...members, item.event],
      };
    } else {
      collapsed.push(item);
    }
  }

  const feed: FeedItem[] = [];
  let currentLabel: string | null = null;
  for (const item of collapsed) {
    const label = dayLabel(item.at, now);
    if (label !== currentLabel) {
      feed.push({ type: 'day', id: `day:${item.id}`, label });
      currentLabel = label;
    }
    feed.push(item);
  }
  return feed;
}
